/**
 * ComplaintSubmissionPage — citizen-facing complaint form.
 *
 * Wired endpoint: `POST /v1/service-desk/cases-mine` on service-service-desk.
 *
 * RBAC: citizen JWTs carry `Complaint/create-own` (seeded to the `customer`
 * role via 20260508_customer_complaint_perms.sql). The CitizenCaseController
 * enforces ownership by stamping the case with the JWT's `upmp_user_id`.
 *
 * The DTO contract requires a `formKey` matching a service-desk catalog entry.
 * We map the citizen-facing category to an UPPER_SNAKE_CASE formKey that is
 * seeded by the catalog bootstrapper (COMPLAINT_* entries added alongside this
 * fix). On success, the case reference is shown to the citizen.
 */
import { useMemo, useState, type FormEvent, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Clock, Loader2, Send } from 'lucide-react';

import { ApiError } from '../../lib/api-client';
import { openCitizenCase, type CaseResponse, type CasePriority, type OpenCaseRequest } from '../../lib/api/service-desk';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import { PageHeader } from './PortalUI';

const inputClass =
  'w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green';

interface CategoryOption {
  readonly value: string;
  readonly label: string;
  /** UPPER_SNAKE_CASE formKey passed to service-desk. Catalog entries are
   *  expected to follow this pattern (regex `/^[A-Z][A-Z0-9_]*$/`). */
  readonly formKey: string;
}

const CATEGORIES: readonly CategoryOption[] = [
  { value: 'DELIVERY_DELAY',  label: 'Delivery delay',  formKey: 'COMPLAINT_DELIVERY_DELAY' },
  { value: 'DAMAGED_ITEM',    label: 'Damaged item',    formKey: 'COMPLAINT_DAMAGED_ITEM' },
  { value: 'LOST_ITEM',       label: 'Lost item',       formKey: 'COMPLAINT_LOST_ITEM' },
  { value: 'WRONG_DELIVERY',  label: 'Wrong delivery',  formKey: 'COMPLAINT_WRONG_DELIVERY' },
  { value: 'STAFF_CONDUCT',   label: 'Staff conduct',   formKey: 'COMPLAINT_STAFF_CONDUCT' },
  { value: 'BILLING_ERROR',   label: 'Billing error',   formKey: 'COMPLAINT_BILLING_ERROR' },
  { value: 'OTHER',           label: 'Other',           formKey: 'COMPLAINT_OTHER' },
];

const SEVERITIES: readonly { readonly value: CasePriority; readonly label: string; readonly desc: string; readonly cls: string }[] = [
  { value: 'LOW',      label: 'Low',      desc: 'Minor issue, not urgent.',                       cls: 'text-green-700 bg-green-50 border-green-200' },
  { value: 'MEDIUM',   label: 'Medium',   desc: 'Moderate issue affecting service.',              cls: 'text-amber-700 bg-amber-50 border-amber-200' },
  { value: 'HIGH',     label: 'High',     desc: 'Significant issue requiring prompt attention.',  cls: 'text-orange-700 bg-orange-50 border-orange-200' },
  { value: 'CRITICAL', label: 'Critical', desc: 'Severe issue — financial loss or safety risk.',  cls: 'text-red-700 bg-red-50 border-red-200' },
];

interface FormState {
  readonly categoryValue: string;
  readonly trackingNumber: string;
  readonly subject: string;
  readonly description: string;
  readonly severity: CasePriority | '';
  readonly contactPrefs: { readonly email: boolean; readonly sms: boolean; readonly phone: boolean };
}

const INITIAL: FormState = {
  categoryValue: '',
  trackingNumber: '',
  subject: '',
  description: '',
  severity: '',
  contactPrefs: { email: true, sms: false, phone: false },
};

export function ComplaintSubmissionPage(): JSX.Element {
  const { token } = useCustomerAuth();
  const [form, setForm] = useState<FormState>(INITIAL);

  const mutation = useMutation<CaseResponse, Error, OpenCaseRequest>({
    mutationFn: (body) => openCitizenCase(body, { bearerToken: token ?? undefined }),
  });

  const descLength = form.description.length;
  const canSubmit = useMemo<boolean>(
    () =>
      form.categoryValue !== '' &&
      form.subject.trim().length > 0 &&
      descLength >= 50 &&
      form.severity !== '' &&
      !mutation.isPending,
    [form, descLength, mutation.isPending],
  );

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!canSubmit) return;
    const category = CATEGORIES.find((c) => c.value === form.categoryValue);
    if (!category) return;
    const body: OpenCaseRequest = {
      formKey: category.formKey,
      title: form.subject.trim().slice(0, 200),
      formData: {
        category: form.categoryValue,
        description: form.description,
        contactPrefs: form.contactPrefs,
        ...(form.trackingNumber.trim().length > 0 ? { trackingNumber: form.trackingNumber.trim() } : {}),
      },
      ...(form.severity !== '' ? { priority: form.severity } : {}),
      ...(form.trackingNumber.trim().length > 0 ? { subjectRef: form.trackingNumber.trim() } : {}),
    };
    mutation.mutate(body);
  }

  if (mutation.isSuccess) {
    return (
      <div>
        <PageHeader title="Complaint Submitted" description="We have received your complaint." />
        <div className="max-w-2xl mx-auto bg-white rounded-xl border border-border p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-nipost-green flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Case opened</h2>
          <p className="text-sm text-muted mb-4">
            Our service desk team will review and respond within the SLA window.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-50 border border-border rounded-lg mb-6">
            <span className="text-xs text-muted">Reference:</span>
            <span className="text-sm font-mono font-bold text-gray-900">{mutation.data.caseNumber}</span>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            <Link
              to="/portal/complaints"
              className="px-6 py-3 bg-nipost-green text-white font-semibold rounded-xl hover:bg-nipost-green-dark text-center"
            >
              View my complaints
            </Link>
            <button
              type="button"
              onClick={() => {
                mutation.reset();
                setForm(INITIAL);
              }}
              className="px-6 py-3 bg-white border border-border text-gray-700 font-semibold rounded-xl hover:bg-gray-50"
            >
              Submit another complaint
            </button>
          </div>
        </div>
      </div>
    );
  }

  const errorMessage = (() => {
    if (!mutation.isError) return null;
    if (mutation.error instanceof ApiError && mutation.error.status === 401) {
      return 'Your session has expired. Please sign in again.';
    }
    if (mutation.error instanceof ApiError && mutation.error.status === 403) {
      return 'You do not have permission to file a complaint. Please contact support.';
    }
    if (mutation.error instanceof ApiError && mutation.error.status === 422) {
      return `Submission could not be processed: ${mutation.error.message}. Please check your form and try again.`;
    }
    if (mutation.error instanceof ApiError && mutation.error.status === 400) {
      return `Validation failed: ${mutation.error.message}. Please check your input and try again.`;
    }
    return mutation.error.message;
  })();

  return (
    <div>
      <PageHeader title="Submit a Complaint" description="Open a service desk case." />

      <div className="max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-border p-6 sm:p-8 space-y-5" noValidate>
          <div>
            <label htmlFor="cs-category" className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-danger">*</span>
            </label>
            <select
              id="cs-category"
              value={form.categoryValue}
              onChange={(e) => setForm((s) => ({ ...s, categoryValue: e.target.value }))}
              className={inputClass}
            >
              <option value="">Select a category</option>
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="cs-tracking" className="block text-sm font-medium text-gray-700 mb-1">
              Tracking number (optional)
            </label>
            <input
              id="cs-tracking"
              type="text"
              value={form.trackingNumber}
              onChange={(e) => setForm((s) => ({ ...s, trackingNumber: e.target.value.toUpperCase() }))}
              placeholder="e.g. EE123456785NG"
              className={`${inputClass} font-mono`}
            />
          </div>

          <div>
            <label htmlFor="cs-subject" className="block text-sm font-medium text-gray-700 mb-1">
              Subject <span className="text-danger">*</span>
            </label>
            <input
              id="cs-subject"
              type="text"
              value={form.subject}
              onChange={(e) => setForm((s) => ({ ...s, subject: e.target.value }))}
              placeholder="Brief summary of the issue"
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="cs-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-danger">*</span>
            </label>
            <textarea
              id="cs-description"
              rows={5}
              value={form.description}
              onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
              placeholder="Please describe the issue in detail..."
              className={`${inputClass} resize-none`}
            />
            <div className="flex items-center justify-between mt-1">
              <p className={`text-xs ${descLength >= 50 ? 'text-success' : 'text-muted'}`}>
                {descLength >= 50 ? 'Minimum met.' : `Need at least ${50 - descLength} more characters.`}
              </p>
              <span className="text-xs text-muted">{descLength} / 50</span>
            </div>
          </div>

          <fieldset>
            <legend className="block text-sm font-medium text-gray-700 mb-2">
              Severity <span className="text-danger">*</span>
            </legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SEVERITIES.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
                    form.severity === opt.value ? `${opt.cls} border` : 'border-border hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="severity"
                    value={opt.value}
                    checked={form.severity === opt.value}
                    onChange={() => setForm((s) => ({ ...s, severity: opt.value }))}
                    className="mt-0.5 w-4 h-4 text-nipost-green focus:ring-nipost-green"
                  />
                  <div>
                    <span className="text-sm font-medium">{opt.label}</span>
                    <p className="text-xs text-muted mt-0.5">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="block text-sm font-medium text-gray-700 mb-2">How should we contact you?</legend>
            <div className="flex flex-wrap gap-3">
              {(['email', 'sms', 'phone'] as const).map((ch) => (
                <label key={ch} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.contactPrefs[ch]}
                    onChange={() =>
                      setForm((s) => ({
                        ...s,
                        contactPrefs: { ...s.contactPrefs, [ch]: !s.contactPrefs[ch] },
                      }))
                    }
                    className="w-4 h-4 rounded border-border text-nipost-green focus:ring-nipost-green"
                  />
                  <span className="text-sm text-gray-700 capitalize">{ch === 'sms' ? 'SMS' : ch}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="p-4 bg-gray-50 border border-border rounded-lg flex items-start gap-2">
            <Clock className="w-4 h-4 text-muted mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-900">Expected response</p>
              <p className="text-xs text-muted">24h for HIGH/CRITICAL · 48h for MEDIUM · 72h for LOW.</p>
            </div>
          </div>

          {errorMessage ? (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700" role="alert">
              {errorMessage}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={!canSubmit}
            className={`flex items-center gap-2 w-full justify-center px-6 py-3 rounded-xl font-semibold transition-colors ${
              canSubmit ? 'bg-nipost-green text-white hover:bg-nipost-green-dark' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {mutation.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            Submit complaint
          </button>
        </form>
      </div>
    </div>
  );
}
